import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BarChart3, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function LoginPage() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const { login } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        const result = await login(email, password);
        setLoading(false);
        if (result.success) {
            toast.success("Welcome back!");
            navigate("/dashboard");
        } else {
            setError(result.error);
        }
    };

    return (
        <div className="min-h-screen bg-[#0A0A0A] flex">
            {/* Left panel */}
            <div className="hidden lg:flex lg:w-1/2 bg-[#121212] border-r border-white/10 items-center justify-center p-12">
                <div className="max-w-md">
                    <div className="flex items-center gap-2 mb-8">
                        <BarChart3 className="w-8 h-8 text-[#007AFF]" />
                        <span className="text-2xl font-bold tracking-tight text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>TradeNalysis</span>
                    </div>
                    <h2 className="text-3xl font-bold tracking-tight text-white mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
                        AI-Powered Trading Analysis
                    </h2>
                    <p className="text-zinc-400 text-sm leading-relaxed">
                        Get precise entry points, stop losses, and take profit targets for stocks, forex, crypto, and futures.
                    </p>
                </div>
            </div>

            {/* Right panel - form */}
            <div className="flex-1 flex items-center justify-center p-6">
                <div className="w-full max-w-sm">
                    <div className="lg:hidden flex items-center gap-2 mb-8">
                        <BarChart3 className="w-6 h-6 text-[#007AFF]" />
                        <span className="text-lg font-bold text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>TradeNalysis</span>
                    </div>

                    <h1 className="text-2xl font-bold tracking-tight text-white mb-1" style={{ fontFamily: 'Chivo, sans-serif' }}>
                        Welcome back
                    </h1>
                    <p className="text-sm text-zinc-500 mb-8">Sign in to your account</p>

                    {error && (
                        <div className="bg-[#FF3B30]/10 border border-[#FF3B30]/30 text-[#FF3B30] text-sm px-4 py-3 mb-6" data-testid="login-error">
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
                        <div>
                            <Label className="text-xs tracking-[0.1em] uppercase font-bold text-zinc-500 mb-2 block">Email</Label>
                            <Input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="bg-transparent border-white/10 focus:border-[#007AFF] focus:ring-1 focus:ring-[#007AFF] rounded-xl text-white h-11"
                                placeholder="you@example.com"
                                required
                                data-testid="login-email-input"
                            />
                        </div>
                        <div>
                            <Label className="text-xs tracking-[0.1em] uppercase font-bold text-zinc-500 mb-2 block">Password</Label>
                            <Input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="bg-transparent border-white/10 focus:border-[#007AFF] focus:ring-1 focus:ring-[#007AFF] rounded-xl text-white h-11"
                                placeholder="Enter your password"
                                required
                                data-testid="login-password-input"
                            />
                        </div>
                        <Button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-xl h-11 font-bold"
                            data-testid="login-submit-btn"
                        >
                            {loading ? "Signing in..." : "Sign In"} {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
                        </Button>
                    </form>

                    <p className="text-sm text-zinc-500 mt-6 text-center">
                        Don't have an account?{" "}
                        <Link to="/register" className="text-[#007AFF] hover:text-[#3395FF] font-medium" data-testid="login-register-link">
                            Start free trial
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
